/**
 * Custom hook for managing chat state and API calls.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import {
  useTranslucenceChatMessageCreateMutation,
  useLazyTranslucenceChatTaskStatusQuery,
  useTranslucenceChatActionCreateMutation,
} from "@app/api/carApi.manual";
import type {
  TranslucenceChatMessageResponse,
  TranslucenceChatTaskResponse,
  ProgressMessage,
  CompleteMessage,
  ErrorMessage,
  C1ComponentsMessage,
} from "@app/api/carApi.manual";
import { MessageType } from "@app/api/carApi.manual";
import type { ChatMessage } from "./types";
import { useChatProgress } from "./useChatProgress";
import { createErrorMessage, createUserMessage } from "./utils/messageHelpers";
import {
  POLLING_INTERVAL_MS,
  POLLING_TIMEOUT_MS,
  MESSAGE_ID_PREFIXES,
} from "./constants/chatConstants";
import type { C1Component, ToolCall, C1Action } from "./types/c1Types";
import { isToolCall } from "./types/c1Types";
import {
  getComponentId,
  mergeComponents,
  deduplicateComponents,
} from "./utils/componentDeduplication";
import { createChatMessageFromResponse } from "./handlers/messageBuilder";
import { useChatMessages } from "./hooks/useChatMessages";
import { useChatPolling } from "./hooks/useChatPolling";

export const useChat = (
  planResultRootId: string,
  planResultTypeId: string
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);

  const [sendMessageMutation] = useTranslucenceChatMessageCreateMutation();
  const [actionMutation] = useTranslucenceChatActionCreateMutation();

  // Use message management hook
  const {
    messages,
    setMessages,
    addMessage,
    addErrorMessage,
    clearMessages: clearMessagesInternal,
    renderedComponentIds: renderedComponentIdsRef,
    processedRequestIds: processedRequestIdsRef,
    processedTaskIds: processedTaskIdsRef,
    streamingMessageId: streamingMessageIdRef,
    pendingStateUpdate: pendingStateUpdateRef,
  } = useChatMessages();

  // Use ref for currentRequestId to avoid stale closures
  const currentRequestIdRef = useRef<string | null>(null);
  
  // Track which request_ids have already created messages (prevents React Strict Mode duplicates)
  const createdMessageRequestIdsRef = useRef<Set<string>>(new Set());
  const loggedMessageIdsRef = useRef<Set<string>>(new Set()); // Track which message IDs we've logged to prevent duplicate logs
  
  // Sync ref with state
  useEffect(() => {
    currentRequestIdRef.current = currentRequestId;
  }, [currentRequestId]);

  // Use progress hook for real-time updates
  const {
    progress: progressUpdate,
    isConnected: isProgressConnected,
    error: progressError,
    currentStep,
    lastThinkItem,
  } = useChatProgress(planResultRootId, planResultTypeId, currentRequestId);

  // Helper to process chat response and add to messages
  const processChatResponse = useCallback(
    (response: TranslucenceChatMessageResponse, taskId?: string) => {
      // Prevent duplicate processing from polling
      if (taskId && processedTaskIdsRef.current.has(taskId)) {
        return;
      }
      if (taskId) {
        processedTaskIdsRef.current.add(taskId);
      }
      
      // Process chat response and create message
      
      // Check if this request was already processed via COMPLETE message
      // If so, skip creating a duplicate message
      if (response.request_id && processedRequestIdsRef.current.has(response.request_id)) {
        return;
      }
      
      // Deduplicate components before creating message
      const responseComponents = response.c1_components || [];
      const newComponents = responseComponents.filter((component: C1Component) => {
        const componentId = getComponentId(component);
        if (renderedComponentIdsRef.current.has(componentId)) {
          return false;
        }
        renderedComponentIdsRef.current.add(componentId);
        return true;
      });
      
      // Note: Some components were filtered as duplicates (already rendered)
      if (responseComponents.length > newComponents.length) {
        // Intentionally empty - components already rendered
      }
      
      // If all components are duplicates, don't create a duplicate message
      // This prevents creating a text-only message when COMPLETE already created one with components
      // COMPLETE message will have both content and components, so we don't need processChatResponse
      if (newComponents.length === 0 && responseComponents.length > 0) {
        return;
      }
      
      const assistantMessage: ChatMessage = {
        id: `${MESSAGE_ID_PREFIXES.ASSISTANT}-${Date.now()}-${Math.random()}`, // Add random to prevent duplicate keys
        role: "assistant",
        content: response.response || "",
        timestamp: new Date(),
        c1Components: deduplicateComponents(newComponents), // Use deduplicated components
        toolCalls: response.tool_calls
          ?.filter((tc): tc is ToolCall & { arguments: Record<string, unknown> } => 
            isToolCall(tc) && Boolean(tc.tool_name) && tc.arguments !== undefined
          )
          .map((tc) => ({
            id: `${MESSAGE_ID_PREFIXES.TOOL}-${Date.now()}-${Math.random()}`,
            name: tc.tool_name,
            arguments: tc.arguments,
            result: tc.result,
            error: tc.error,
          })) || [],
        feedbackEnabled: response.feedback_enabled ?? false,
        feedbackRequestId: response.request_id,
      };

      addMessage(assistantMessage);
    },
    // Refs intentionally omitted - they're stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addMessage]
  );

  // Track last processed progress update to prevent duplicate processing in React Strict Mode
  const lastProcessedProgressRef = useRef<ProgressMessage | null>(null);
  const processedProgressKeysRef = useRef<Set<string>>(new Set());
  
  // Handle progress updates from SSE
  useEffect(() => {
    if (!progressUpdate) {
      return;
    }
    
    // Prevent processing the same progress update twice (React Strict Mode)
    // Compare by reference first (fastest check)
    if (lastProcessedProgressRef.current === progressUpdate) {
      return;
    }
    
    // Also check by content hash to handle cases where useChatProgress creates new objects
    // Use request_id + type + a hash of key fields to uniquely identify each update
    const requestId = currentRequestIdRef.current || currentRequestId;
    let progressKey: string;
    
    if (progressUpdate.type === MessageType.C1_COMPONENTS) {
      const c1Msg = progressUpdate as C1ComponentsMessage;
      const componentCount = c1Msg.components?.length || 0;
      progressKey = `${requestId || 'no-request'}_c1_${componentCount}`;
    } else if (progressUpdate.type === MessageType.COMPLETE) {
      const completeMsg = progressUpdate as CompleteMessage;
      progressKey = `${requestId || 'no-request'}_complete_${completeMsg.result?.request_id || 'no-id'}`;
    } else {
      // For other types, use type + request_id
      progressKey = `${requestId || 'no-request'}_${progressUpdate.type}`;
    }
    
    // Check if we've already processed this exact progress update
    // BUT allow updates to existing messages even if we've seen this progress before
    const alreadyProcessed = processedProgressKeysRef.current.has(progressKey);
    
    // Mark as processed (even if already processed, to track it)
    lastProcessedProgressRef.current = progressUpdate;
    if (!alreadyProcessed) {
      processedProgressKeysRef.current.add(progressKey);
    }
    
    // Clean up old keys (keep last 50)
    if (processedProgressKeysRef.current.size > 50) {
      const keysArray = Array.from(processedProgressKeysRef.current);
      processedProgressKeysRef.current = new Set(keysArray.slice(-25));
    }


    // Handle C1_COMPONENTS message - progressive component rendering
    // Use type guard to check for C1_COMPONENTS message
    if (progressUpdate.type === MessageType.C1_COMPONENTS) {
      // Don't process SSE components if COMPLETE was already received for this request
      const requestId = currentRequestIdRef.current || currentRequestId;
      if (requestId && processedRequestIdsRef.current.has(requestId)) {
        return;
      }
      
      // If we've already processed this exact progress update, only continue if updating existing message
      if (alreadyProcessed) {
        const currentStreamingId = streamingMessageIdRef.current;
        const hasExistingStreamingMessage = currentStreamingId && messages.some((msg) => msg.id === currentStreamingId);
        if (!hasExistingStreamingMessage) {
          // No existing message to update, skip duplicate processing
          return;
        }
        // Continue to update existing message
      }
      
      const c1Message = progressUpdate as C1ComponentsMessage;
      const components = c1Message.components || [];
      
      if (components.length > 0) {
        // CRITICAL: Do NOT deduplicate when updating UI components
        // Pass ALL components to mergeComponents - it will update existing components
        // and add new ones. This allows components to be updated with new data.
        
        // Track component IDs for logging, but don't filter them out
        components.forEach((component: C1Component) => {
          const componentId = getComponentId(component);
          if (!renderedComponentIdsRef.current.has(componentId)) {
            renderedComponentIdsRef.current.add(componentId);
          }
        });
        
        // Get or create streaming message
        // Set ref BEFORE setMessages to prevent duplicate creation in React Strict Mode
        const currentStreamingId = streamingMessageIdRef.current;
        
        if (!currentStreamingId) {
          // Generate ID before setMessages to ensure consistency across React Strict Mode double renders
          const newStreamingId = `${MESSAGE_ID_PREFIXES.ASSISTANT}-${Date.now()}-${Math.random()}`;
          streamingMessageIdRef.current = newStreamingId;
        }
        
        setMessages((prev) => {
          const streamingId = streamingMessageIdRef.current;
          
          if (!streamingId) {
            // Shouldn't happen, but defensive
            return prev;
          }
          
          // Check if message already exists (handles React Strict Mode double renders)
          const existingMessage = prev.find((msg) => msg.id === streamingId);
          if (existingMessage) {
            // Update existing streaming message - merge ALL components (updates existing, adds new)
            const existingComponents = existingMessage.c1Components || [];
            const mergedComponents = mergeComponents(existingComponents, components);
            const updatedComponentCount = mergedComponents.length;
            console.log(`[Chat] 🔄 Streaming UPDATE: Message ${streamingId} now has ${updatedComponentCount} component(s), isStreaming: true`);
            return prev.map((msg) => {
              if (msg.id === streamingId) {
                return {
                  ...msg,
                  // Preserve existing content - don't overwrite with empty
                  content: msg.content || "",
                  c1Components: mergedComponents,
                  isStreaming: true, // Keep streaming state when updating with new components
                };
              }
              return msg;
            });
          }
          
          // Double-check: prevent duplicate if message was added between ref set and this callback
          // This handles React Strict Mode where setMessages callback can run twice
          const alreadyInState = prev.some((msg) => msg.id === streamingId);
          if (alreadyInState) {
            // Message already exists, update it instead
            return prev.map((msg) => {
              if (msg.id === streamingId) {
                const existingComponents = msg.c1Components || [];
                const mergedComponents = mergeComponents(existingComponents, components);
                return {
                  ...msg,
                  c1Components: mergedComponents,
                  isStreaming: true,
                };
              }
              return msg;
            });
          }
          
          // Create new streaming message if none exists
          // Only log once per message ID (React Strict Mode guard)
          if (!loggedMessageIdsRef.current.has(`${streamingId}_started`)) {
            console.log(`[Chat] 🟢 Streaming STARTED: Created message ${streamingId} with ${components.length} component(s), isStreaming: true`);
            loggedMessageIdsRef.current.add(`${streamingId}_started`);
          }
          const newMessage: ChatMessage = {
            id: streamingId,
            role: "assistant",
            content: "",
            timestamp: new Date(),
            c1Components: components, // Use all components directly - mergeComponents will handle deduplication within the array
            isStreaming: true, // Mark as streaming when components arrive via SSE
          };
          return [...prev, newMessage];
        });
      }
    }

    // Handle complete message - process final response
    if (progressUpdate.type === MessageType.COMPLETE) {
      // If we've already processed this exact progress update, only continue if updating existing message
      if (alreadyProcessed) {
        const currentStreamingId = streamingMessageIdRef.current;
        const hasExistingStreamingMessage = currentStreamingId && messages.some((msg: ChatMessage) => msg.id === currentStreamingId);
        if (!hasExistingStreamingMessage) {
          // Check if message exists by request_id
          const requestId = currentRequestIdRef.current || currentRequestId;
          const hasExistingByRequestId = requestId && messages.some((msg) => msg.feedbackRequestId === requestId);
          if (!hasExistingByRequestId) {
            // No existing message to update, skip duplicate processing
            return;
          }
        }
        // Continue to update existing message
      }
      const completeMessage = progressUpdate as CompleteMessage;
      
      // Prevent processing COMPLETE multiple times (due to re-renders)
      // Use ref to get current value (avoid stale closure)
      const requestId = currentRequestIdRef.current || currentRequestId;
      if (!requestId) {
        return;
      }
      
      // Check if already processed - MUST check before marking to prevent React Strict Mode duplicates
      if (processedRequestIdsRef.current.has(requestId)) {
        return;
      }
      
      // Mark as processed IMMEDIATELY to prevent duplicate processing (before any state updates)
      // This MUST happen synchronously before any async operations or state updates
      processedRequestIdsRef.current.add(requestId);
      
      // Stop polling immediately since we've received COMPLETE via SSE
      stopPolling();
      
      // NOTE: Don't check messages state here - it's from closure and may be stale in React Strict Mode
      // Check inside setMessages callback using prev instead
      
      if (completeMessage.result) {
        // Get components from COMPLETE message
        const completeComponents = completeMessage.result.c1_components || [];
        
        // CRITICAL: Keep wrapper format { component: {...}, error: null } for SSE components
        // The C1Component SDK expects the full wrapper format, not the extracted inner component
        // This matches the POST response format exactly
        // NOTE: We do NOT use deduplicateComponents here because it extracts the inner component,
        // which breaks rendering. The SDK handles extraction internally.
        const deduplicatedCompleteComponents = completeComponents; // Keep wrapper format
        
        // Track component IDs for logging, but don't filter them out
        // mergeComponents will handle updating existing components and adding new ones
        deduplicatedCompleteComponents.forEach((component: C1Component) => {
          const componentId = getComponentId(component);
          if (!renderedComponentIdsRef.current.has(componentId)) {
            renderedComponentIdsRef.current.add(componentId);
          }
        });
        
        // Process COMPLETE message - update streaming message or create new
        const currentStreamingId = streamingMessageIdRef.current;
        
        // Check if we've already queued a state update for this request
        const stateUpdateKey = `${requestId}_${currentStreamingId || 'new'}`;
        if (pendingStateUpdateRef.current.has(stateUpdateKey)) {
          return;
        }
        
        // NOTE: Don't check messages state here - it's from closure and may be stale in React Strict Mode
        // We check inside setMessages callback using prev instead (which is always current)
        // The check for existing message by requestId happens inside setMessages callback
        
        // Mark as pending to prevent duplicate state updates
        pendingStateUpdateRef.current.add(stateUpdateKey);
        
        setMessages((prev: ChatMessage[]) => {
          // Double-check inside the callback (defensive - React Strict Mode can cause double calls)
          const executedKey = stateUpdateKey + "_executed";
          
          // Check again inside callback (state might have updated)
          const existingInState = requestId 
            ? prev.find((msg: ChatMessage) => msg.feedbackRequestId === requestId)
            : null;
          
          if (existingInState && !currentStreamingId) {
            // Message already exists - update it instead of creating duplicate
            // CRITICAL: Merge components to allow updates to existing ones
            console.log(`[Chat] 🔄 Updating existing message ${existingInState.id} from COMPLETE`);
            return prev.map((msg) => {
              if (msg.id === existingInState.id) {
                const existingComponents = msg.c1Components || [];
                const mergedComponents = mergeComponents(existingComponents, deduplicatedCompleteComponents);
                return {
                  ...msg,
                  content: completeMessage.result.response || msg.content || "",
                  c1Components: mergedComponents.length > 0 ? mergedComponents : existingComponents,
                  isStreaming: false,
                };
              }
              return msg;
            });
          }
          
          if (pendingStateUpdateRef.current.has(executedKey)) {
            // Don't skip if this update would add content that's missing
            const existingMessage = currentStreamingId ? prev.find((msg: ChatMessage) => msg.id === currentStreamingId) : null;
            const hasContentInUpdate = completeMessage.result.response?.trim();
            const missingContentInState = existingMessage && !existingMessage.content?.trim();
            
            if (hasContentInUpdate && missingContentInState) {
              // Continue with the update to add the content - don't return early
            } else {
              // Check if content is already set - if so, we can safely skip
              const contentAlreadySet = existingMessage?.content?.trim();
              if (contentAlreadySet) {
                console.log(`[Chat] ⚠️ Skipping duplicate COMPLETE update (content already set)`);
                return prev;
              } else {
                // Content not set yet, allow the update
                // Continue with the update
              }
            }
          }
          // Mark as executed AFTER we've decided to proceed
          pendingStateUpdateRef.current.add(executedKey);
          
          const existingMessage = currentStreamingId ? prev.find((msg: ChatMessage) => msg.id === currentStreamingId) : null;
          
          if (existingMessage) {
            // Merge with existing components, updating existing ones instead of duplicating
            // CRITICAL: Use ALL components from COMPLETE - mergeComponents will update existing ones
            const existingComponents = existingMessage.c1Components || [];
            // Merge components - this will update existing components with new versions if they have the same structure
            const allComponents = mergeComponents(existingComponents, deduplicatedCompleteComponents);
            
            streamingMessageIdRef.current = null;
            // Always use COMPLETE response content if available and non-empty
            // Otherwise preserve existing content (don't clear it)
            const completeResponse = completeMessage.result.response?.trim();
            const finalContent = completeResponse || existingMessage.content || "";
            // Only log once per message ID (React Strict Mode guard)
            if (!loggedMessageIdsRef.current.has(`${currentStreamingId}_complete`)) {
              console.log(`[Chat] 🔴 Streaming COMPLETE: Message ${currentStreamingId} finished with ${allComponents.length} component(s), isStreaming: false`);
              loggedMessageIdsRef.current.add(`${currentStreamingId}_complete`);
            }
            const updatedMessages = prev.map((msg: ChatMessage) => 
              msg.id === currentStreamingId
                ? {
                    ...msg,
                    // Always preserve content - use COMPLETE if available, otherwise keep existing
                    content: finalContent,
                    c1Components: allComponents.length > 0 ? allComponents : existingComponents,
                    isStreaming: false, // Mark as complete when COMPLETE message received
                    toolCalls: completeMessage.result.tool_calls
                      ?.filter((tc): tc is ToolCall & { arguments: Record<string, unknown> } => 
                        isToolCall(tc) && Boolean(tc.tool_name) && tc.arguments !== undefined
                      )
                      .map((tc) => ({
                        id: `${MESSAGE_ID_PREFIXES.TOOL}-${Date.now()}-${Math.random()}`,
                        name: tc.tool_name,
                        arguments: tc.arguments,
                        result: tc.result,
                        error: tc.error,
                      })) || msg.toolCalls || [],
                    feedbackEnabled: completeMessage.result.feedback_enabled ?? false,
                    feedbackRequestId: completeMessage.result.request_id,
                  }
                : msg
            );
            console.log(`[Chat] 📊 Updated streaming message. Total messages: ${updatedMessages.length}`);
            return updatedMessages;
          } else {
            // No streaming message found, create new message
            // Double-check inside callback (React Strict Mode can cause double calls)
            const checkRequestId = currentRequestIdRef.current || currentRequestId;
            
            // CRITICAL: Check if message already exists in STATE first (not just ref)
            // React Strict Mode can cause double renders, and ref might be set before state commits
            const messageExistsInState = checkRequestId 
              ? prev.some((msg) => msg.feedbackRequestId === checkRequestId)
              : false;
            
            if (messageExistsInState) {
              // Message already exists in state - don't create duplicate
              console.log(`[Chat] ⚠️ Message already exists in state for requestId ${checkRequestId}, skipping`);
              return prev;
            }
            
            // Also check ref as secondary guard, but only if message doesn't exist in state
            // This handles the case where ref was set but state update hasn't committed yet
            if (checkRequestId && createdMessageRequestIdsRef.current.has(checkRequestId)) {
              // Ref says it was created, but message isn't in state yet
              // This can happen in React Strict Mode - wait for state to catch up
              console.log(`[Chat] ⚠️ Ref says message created for requestId ${checkRequestId}, but not in state yet. Checking state...`);
              // Don't skip - let the state update proceed
            }
            
            streamingMessageIdRef.current = null;
            const newMessageId = `${MESSAGE_ID_PREFIXES.ASSISTANT}-${Date.now()}-${Math.random()}`;
            
            // CRITICAL: Convert components to strings to match POST response format
            // The SDK expects the full wrapper format { component: {...}, error: null }
            // We keep the wrapper format (don't extract inner component) to match POST exactly
            const stringComponents = deduplicatedCompleteComponents.map((comp) => {
              if (typeof comp === 'string') {
                return comp;
              }
              // Convert wrapper object to JSON string (keeps wrapper format)
              return JSON.stringify(comp);
            });
            
            const newMessage: ChatMessage = {
              id: newMessageId,
              role: "assistant",
              content: completeMessage.result.response || "",
              timestamp: new Date(),
              c1Components: stringComponents, // Convert to strings to match POST format
              isStreaming: false, // Not streaming if created directly from COMPLETE
              toolCalls: completeMessage.result.tool_calls
                ?.filter((tc): tc is ToolCall & { arguments: Record<string, unknown> } => 
                  isToolCall(tc) && Boolean(tc.tool_name) && tc.arguments !== undefined
                )
                .map((tc) => ({
                  id: `${MESSAGE_ID_PREFIXES.TOOL}-${Date.now()}-${Math.random()}`,
                  name: tc.tool_name,
                  arguments: tc.arguments,
                  result: tc.result,
                  error: tc.error,
                })) || [],
              feedbackEnabled: completeMessage.result.feedback_enabled ?? false,
              feedbackRequestId: completeMessage.result.request_id,
            };
            
            // Mark as created AFTER message is added to state (prevents duplicates)
            if (checkRequestId) {
              createdMessageRequestIdsRef.current.add(checkRequestId);
            }
            
            const newMessages = [...prev, newMessage];
            console.log(`[Chat] 📊 Created new message. Total messages: ${newMessages.length}`);
            return newMessages;
          }
        });
      }
      setIsLoading(false);
      setCurrentRequestId(null); // Clear request_id after completion
      setError(null); // Clear any error state since request completed successfully
      
      // Stop polling since we've received COMPLETE via SSE
      stopPolling();
      
      // Note: Don't clear renderedComponentIdsRef here - keep them for the session
      // They'll be cleared on next sendMessage
    }


    // Handle error message
    if (progressUpdate.type === MessageType.ERROR) {
      const errorMessage = progressUpdate as ErrorMessage;
      const errorText = errorMessage.error || "An error occurred";
      setError(errorText);
      
      // Mark any streaming message as complete on error
      setMessages((prev: ChatMessage[]) => {
        const streamingId = streamingMessageIdRef.current;
        if (streamingId) {
          console.log(`[Chat] ⚠️ Streaming ERROR: Message ${streamingId} marked as complete (isStreaming: false) due to error`);
          return prev.map((msg) =>
            msg.id === streamingId
              ? { ...msg, isStreaming: false }
              : msg
          );
        }
        return prev;
      });
      
      setMessages((prev: ChatMessage[]) => [...prev, createErrorMessage(errorText)]);
      setIsLoading(false);
      setCurrentRequestId(null); // Clear request_id after error
      streamingMessageIdRef.current = null; // Clear streaming message ID
      // Note: Don't clear renderedComponentIdsRef here - keep them for the session
    }

    // Handle progress error from SSE connection
    if (progressError) {
      // Only show error if we're not already showing a message error
      // and if polling isn't active (fallback)
      if (!isPolling()) {
        setError(progressError.message);
      }
    }
    // currentRequestId intentionally omitted - using ref to avoid stale closures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressUpdate, progressError, processChatResponse]);

  // Handle SSE disconnection - mark streaming messages as complete
  useEffect(() => {
    // If SSE disconnects and we have a streaming message, mark it as complete
    if (!isProgressConnected && streamingMessageIdRef.current) {
      setMessages((prev) => {
        const streamingId = streamingMessageIdRef.current;
        if (streamingId) {
          console.log(`[Chat] 🔌 SSE DISCONNECTED: Message ${streamingId} marked as complete (isStreaming: false)`);
          return prev.map((msg) =>
            msg.id === streamingId
              ? { ...msg, isStreaming: false }
              : msg
          );
        }
        return prev;
      });
    }
  }, [isProgressConnected]);

  // Use polling hook
  const { startPolling, stopPolling, isPolling } = useChatPolling(
    planResultRootId,
    planResultTypeId,
    {
      onSuccess: (result, taskId) => {
        processChatResponse(result, taskId);
        setIsLoading(false);
      },
      onFailure: (errorMessage: string) => {
        // Only set error if SSE is not connected (polling is fallback)
        // If SSE is connected, it will handle errors via ERROR message type
        if (!isProgressConnected) {
          setError(errorMessage);
          addErrorMessage(errorMessage);
          setIsLoading(false);
        }
      },
      onError: (errorMessage: string) => {
        // Only set error if SSE is not connected (polling is fallback)
        // If SSE is connected, it will handle errors via ERROR message type
        if (!isProgressConnected) {
          setError(errorMessage);
          setIsLoading(false);
        }
      },
      onTimeout: () => {
        // Only set timeout error if SSE is not connected
        // If SSE is connected, it will handle completion
        if (!isProgressConnected) {
          setIsLoading(false);
          const errorMessage = "Request timed out. Please try again.";
          setError(errorMessage);
          addErrorMessage(errorMessage);
        }
      },
      isRequestProcessed: (requestId: string | null) => {
        if (!requestId) return false;
        return processedRequestIdsRef.current.has(requestId);
      },
      getCurrentRequestId: () => currentRequestIdRef.current || currentRequestId,
    }
  );

  /**
   * Handles continue_conversation actions from C1 components.
   * 
   * When a user interacts with a C1 component (e.g., clicks a chart segment),
   * this function sends the action to the backend action endpoint, which
   * processes it with conversation context and returns updated C1 components.
   * 
   * The function:
   * 1. Validates that a request_id exists (active conversation)
   * 2. Extracts action parameters (llmFriendlyMessage, humanFriendlyMessage)
   * 3. Calls the action endpoint with the current request_id
   * 4. Adds user message to chat history (displaying humanFriendlyMessage)
   * 5. Processes the assistant response (adds to chat history with C1 components)
   * 6. Maintains the same request_id for conversation continuity
   * 7. Handles errors appropriately (404 context expiry, 403 access denied, etc.)
   * 
   * @param action - C1 action object from Thesys SDK
   * 
   * @example
   * ```tsx
   * handleContinueConversation({
   *   params: {
   *     llmFriendlyMessage: "Show details for IRA (value: $500,000)",
   *     humanFriendlyMessage: "Show details for IRA"
   *   }
   * });
   * ```
   */
  const handleContinueConversation = useCallback(
    async (action: C1Action) => {
      // 1. Validate request_id exists
      if (!currentRequestId) {
        setError("Cannot process action: No active conversation. Please start a new query.");
        console.error("[Chat] Action triggered but no request_id available");
        return;
      }

      // 2. Extract action parameters
      const llmFriendlyMessage = 
        action.params?.llmFriendlyMessage || 
        action.llmFriendlyMessage || 
        "";
      const humanFriendlyMessage = 
        action.params?.humanFriendlyMessage || 
        action.humanFriendlyMessage || 
        llmFriendlyMessage; // Fallback to llmFriendlyMessage if humanFriendlyMessage not provided

      if (!llmFriendlyMessage) {
        console.error("[Chat] Action missing llmFriendlyMessage:", action);
        setError("Invalid action: Missing message content.");
        return;
      }

      // 3. Set loading state
      setIsLoading(true);
      setError(null);

      try {
        // 4. Call API endpoint
        const response = await actionMutation({
          planResultRootId,
          planResultTypeId,
          requestId: currentRequestId,
          type: "continue_conversation",
          params: {
            llmFriendlyMessage,
            humanFriendlyMessage,
          },
        }).unwrap();

        // 5. Add user message to chat history (display humanFriendlyMessage)
        const userMessage = createUserMessage(humanFriendlyMessage);
        addMessage(userMessage);

        // 6. Process assistant response (reuse existing logic)
        processChatResponse(response);

        // 7. Update request_id if changed (should remain same for continuation)
        if (response.request_id && response.request_id !== currentRequestId) {
          console.log("[Chat] Action response returned different request_id, updating:", {
            old: currentRequestId,
            new: response.request_id,
          });
          setCurrentRequestId(response.request_id);
          currentRequestIdRef.current = response.request_id;
        }

      } catch (err: unknown) {
        // 8. Handle errors
        console.error("[Chat] Error processing action:", err);
        
        if (err && typeof err === 'object' && 'status' in err) {
          const errorStatus = (err as { status: number }).status;
          
          if (errorStatus === 404) {
            // Context expired
            setError("Conversation context expired. Please start a new query.");
            setCurrentRequestId(null);
            currentRequestIdRef.current = null;
          } else if (errorStatus === 403) {
            // Access denied
            setError("Access denied. Please refresh the page and try again.");
          } else if (errorStatus === 400) {
            // Invalid payload
            setError("Invalid action request. Please try again.");
          } else {
            // Other errors (500, network, etc.)
            setError("Failed to process action. Please try again.");
          }
        } else {
          // Network errors or unknown errors
          setError("Failed to process action. Please check your connection and try again.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentRequestId,
      planResultRootId,
      planResultTypeId,
      actionMutation,
      addMessage,
      processChatResponse,
    ]
  );

  const sendMessage = useCallback(
    async (messageText: string) => {
      if (!messageText.trim() || isLoading) {
        return;
      }

      // Add user message to UI immediately
      const userMessage = createUserMessage(messageText);
      addMessage(userMessage);
      setIsLoading(true);
      setError(null);
      streamingMessageIdRef.current = null; // Clear streaming message ID
      renderedComponentIdsRef.current.clear(); // Clear component IDs on new message
      processedRequestIdsRef.current.clear(); // Clear processed request IDs
      processedTaskIdsRef.current.clear(); // Clear processed task IDs
      currentRequestIdRef.current = null; // Clear request ID ref
      pendingStateUpdateRef.current.clear(); // Clear pending state updates
      createdMessageRequestIdsRef.current.clear(); // Clear created message tracking
      loggedMessageIdsRef.current.clear(); // Clear logged message tracking

      try {
        const response = await sendMessageMutation({
          planResultRootId,
          planResultTypeId,
          message: messageText,
        }).unwrap();

        // Check for request_id to determine mode (per backend guidance)
        // Mode A: request_id is missing or null → use POST response directly
        // Mode B: request_id exists → connect to SSE for progress updates
        if (response.request_id) {
          // Mode B: Has request_id - connect to SSE for real-time progress updates
          setCurrentRequestId(response.request_id);
          currentRequestIdRef.current = response.request_id; // Update ref
          
          // If we also have task_id, start polling as fallback
          if ("task_id" in response && response.task_id) {
            startPolling(response.task_id);
          }
        } else {
          // Mode A: No request_id - use POST response directly (synchronous mode)
          // This happens when:
          // - DISABLE_REDIS_MESSAGING=True in backend settings
          // - Redis is unavailable
          // - Backend fails to initialize RedisMessageService
          console.log("[Chat] Mode A: No request_id, using POST response directly");
          processChatResponse(response as TranslucenceChatMessageResponse);
          setIsLoading(false);
          // Clear any previous request_id
          setCurrentRequestId(null);
          currentRequestIdRef.current = null;
          
          // If we have task_id but no request_id, still poll as fallback
          if ("task_id" in response && response.task_id) {
            startPolling(response.task_id);
          }
        }
      } catch (err: unknown) {
        // Clear polling if it was started
        stopPolling();

        const errorMessage =
          (err as { data?: { error?: string }; message?: string })?.data?.error ||
          (err as { message?: string })?.message ||
          "Failed to send message";
        setError(errorMessage);

        // Add error message to chat
        addErrorMessage(errorMessage);
        setIsLoading(false);
      }
    },
    // Refs intentionally omitted - they're stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      planResultRootId,
      planResultTypeId,
      sendMessageMutation,
      isLoading,
      startPolling,
      processChatResponse,
      addMessage,
      stopPolling,
    ]
  );

  const clearMessages = useCallback(() => {
    // Clear polling on message clear
    stopPolling();
    clearMessagesInternal();
    setError(null);
    setIsLoading(false);
    setCurrentRequestId(null); // Clear request_id
    currentRequestIdRef.current = null; // Clear ref
    createdMessageRequestIdsRef.current.clear(); // Clear created message tracking
    loggedMessageIdsRef.current.clear(); // Clear logged message tracking
  }, [stopPolling, clearMessagesInternal]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    handleContinueConversation,
    // Progress update state
    progressUpdate,
    isProgressConnected,
    currentStep,
    lastThinkItem,
  };
};
